import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/app/api";
import { Card } from "@/app/ui";
import { Check, Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const FIELDS = {
  personal: [["first_name", "First name"], ["last_name", "Last name"], ["preferred_name", "Preferred name"],
    ["date_of_birth", "Date of birth"], ["email", "Email"], ["telephone", "Telephone / mobile"],
    ["preferred_language", "Preferred language"], ["country_of_residence", "Country of residence"],
    ["city_region", "City / region"], ["nationality", "Nationality / citizenship"], ["residency_status", "Residency status"]],
  career: [["employment_status", "Employment status"], ["occupation", "Current / recent occupation"],
    ["industry", "Industry"], ["years_experience", "Years of experience"], ["career_goals", "Career goals"]],
  study_preferences: [["subjects", "Subjects / interests"], ["qualification_level", "Desired qualification level"],
    ["study_mode", "Full-time / part-time"], ["delivery", "Online / on-campus / hybrid"],
    ["locations", "Preferred locations"], ["relocate", "Willing to relocate"], ["start_period", "Preferred start period"]],
  international: [["nationality", "Nationality"], ["country_of_residence", "Country of residence"],
    ["previous_country_of_education", "Previous country of education"], ["english_status", "English-language status"],
    ["english_test", "English test / qualification"], ["qualification_recognition", "Qualification recognition status"],
    ["international_applicant", "International applicant"], ["visa_status", "Visa / support requirement status"]],
};
const SECTION_TITLES = { personal: "Personal information", career: "Career information",
  study_preferences: "Study preferences", international: "International readiness" };

const Field = ({ label, value, onChange }) => (
  <div>
    <label className="text-sm font-medium text-ink">{label}</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20"
      placeholder={label} />
  </div>
);

export default function Profile() {
  const [form, setForm] = useState({ personal: {}, career: {}, study_preferences: {}, international: {}, navigator: {} });
  const [edu, setEdu] = useState([]);
  const [comp, setComp] = useState({ pct: 0, sections: [] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [newEdu, setNewEdu] = useState({ institution: "", country: "", qualification: "", subject: "", level: "", completion_status: "completed", award_status: "awarded", evidence_status: "official_documents" });
  const [searchParams] = useSearchParams();
  const onboarding = searchParams.get("onboarding") === "1";

  const load = useCallback(async () => {
    const { data } = await api.get("/individual/profile");
    const p = data.profile || {};
    setForm({ personal: p.personal || {}, career: p.career || {}, study_preferences: p.study_preferences || {}, international: p.international || {}, navigator: p.navigator || {} });
    setEdu(data.education || []);
    setComp({ pct: data.completion_pct, sections: data.sections });
    setLoaded(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const setField = (section, key) => (val) => { setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try { await api.put("/individual/profile", form); setDirty(false); toast.success("Profile saved");
      const { data } = await api.get("/individual/profile/completion"); setComp({ pct: data.completion_pct, sections: data.sections });
    } catch { toast.error("Could not save profile"); } finally { setSaving(false); }
  };

  const addEdu = async () => {
    if (!newEdu.institution.trim()) { toast.error("Institution is required"); return; }
    await api.post("/individual/education", newEdu);
    setNewEdu({ institution: "", country: "", qualification: "", subject: "", level: "", completion_status: "completed", award_status: "awarded", evidence_status: "official_documents" });
    await load(); toast.success("Education added");
  };
  const delEdu = async (id) => { if (!window.confirm("Delete this education record?")) return; await api.delete(`/individual/education/${id}`); await load(); toast.success("Removed"); };

  if (!loaded) return <p className="text-slatey">Loading profile…</p>;

  return (
    <div data-testid="profile-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Your profile</h1>
          <p className="mt-2 max-w-2xl text-slatey">Your profile builds progressively as you explore opportunities and prepare applications. OpenDegree asks for information only when it is relevant.</p>
        </div>
        <button onClick={save} disabled={saving || !dirty} data-testid="profile-save-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-cobalt px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {onboarding && (
        <Card data-testid="learner-onboarding-banner" className="mt-6 border-cobalt/20 bg-cobalt/5">
          <h2 className="font-display text-xl font-bold text-ink">Welcome to OpenDegree</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">
            Complete your profile and education history once. OpenDegree will reuse this information when you discover and apply to opportunities.
          </p>
          <Link to="/app/discover" data-testid="onboarding-discover-link" className="mt-4 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">
            Discover opportunities
          </Link>
        </Card>
      )}

      <Card className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-ink">Profile completion</h2>
          <span className="text-sm font-semibold text-slatey" data-testid="completion-pct">{comp.pct}%</span>
        </div>
        <div className="mt-3 h-2.5 w-full rounded-full bg-slate-100"><div className="h-2.5 rounded-full bg-cobalt transition-all" style={{ width: `${comp.pct}%` }} /></div>
        <div className="mt-4 flex flex-wrap gap-2">
          {comp.sections.map((s) => (
            <span key={s.key} data-testid={`section-${s.key}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${s.complete ? "bg-emerald-50 text-emerald-600" : s.level === "required" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
              {s.complete ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}{s.label}{!s.complete && s.level === "required" ? " · required" : ""}
            </span>
          ))}
        </div>
      </Card>

      {form.navigator?.journey_answers && (
        <Card className="mt-6 border-cobalt/20 bg-cobalt/5" data-testid="card-navigator">
          <h2 className="font-display text-lg font-bold text-ink">Saved journey preferences</h2>
          <p className="mt-2 text-sm leading-6 text-slatey">
            Funding intention: {(form.navigator.funding_intention || "not provided").replaceAll("_", " ")} · UK status: {(form.navigator.uk_status || "not provided").replaceAll("_", " ")}
          </p>
          <p className="mt-2 text-xs leading-5 text-slatey">Saved only after your explicit confirmation. Used only when relevant to an assessment or application.</p>
        </Card>
      )}

      <Card className="mt-6" data-testid="card-personal">
        <h2 className="font-display text-lg font-bold text-ink">{SECTION_TITLES.personal}</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {FIELDS.personal.map(([key, label]) => (
            <Field key={key} label={label} value={form.personal[key]} onChange={setField("personal", key)} />
          ))}
        </div>
      </Card>

      {[
        ["career", "Add career information when experience matters"],
        ["study_preferences", "Add study preferences to improve recommendations"],
        ["international", form.navigator?.journey_answers?.current_location === "outside_uk" || form.international?.international_applicant === "Yes" ? "International information relevant to your journey" : "Add international information only when needed"],
      ].map(([section, hint]) => (
        <details key={section} data-testid={`card-${section}`} className="group mt-6 rounded-3xl border border-slate-200 bg-white" open={section === "international" && (form.navigator?.journey_answers?.current_location === "outside_uk" || form.international?.international_applicant === "Yes")}>
          <summary className="cursor-pointer list-none p-6" data-testid={`toggle-${section}`}>
            <span className="font-display text-lg font-bold text-ink">{SECTION_TITLES[section]}</span>
            <span className="mt-1 block text-sm text-slatey">{hint}</span>
          </summary>
          <div className="px-6 pb-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS[section].map(([key, label]) => (
                <Field key={key} label={label} value={form[section][key]} onChange={setField(section, key)} />
              ))}
            </div>
            {section === "international" && (
              <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slatey">OpenDegree records your readiness for guidance only and does not provide regulated immigration advice. Specialist visa matters are signposted to your provider or an authorised specialist.</p>
            )}
          </div>
        </details>
      ))}

      <Card className="mt-6" data-testid="card-education">
        <h2 className="font-display text-lg font-bold text-ink">Education history</h2>
        <div className="mt-4 space-y-3">
          {edu.map((e) => (
            <div key={e.id} data-testid={`edu-${e.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4">
              <div className="min-w-0">
                <p className="font-semibold text-ink truncate">{e.institution}</p>
                <p className="text-sm text-slatey truncate">{[e.qualification, e.subject, e.level, e.country].filter(Boolean).join(" · ")}</p>
                <p className="mt-1 text-xs text-slatey">{(e.completion_status || "unknown").replaceAll("_", " ")} · {(e.award_status || "awarded").replaceAll("_", " ")} · evidence: {(e.evidence_status || "unknown").replaceAll("_", " ")}</p>
              </div>
              <button onClick={() => delEdu(e.id)} data-testid={`edu-del-${e.id}`} className="rounded-lg p-2 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {edu.length === 0 && <p className="text-sm text-slatey">No education records yet.</p>}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Institution *" value={newEdu.institution} onChange={(v) => setNewEdu({ ...newEdu, institution: v })} />
          <Field label="Country" value={newEdu.country} onChange={(v) => setNewEdu({ ...newEdu, country: v })} />
          <Field label="Qualification" value={newEdu.qualification} onChange={(v) => setNewEdu({ ...newEdu, qualification: v })} />
          <Field label="Subject / field" value={newEdu.subject} onChange={(v) => setNewEdu({ ...newEdu, subject: v })} />
          <Field label="Level" value={newEdu.level} onChange={(v) => setNewEdu({ ...newEdu, level: v })} />
          <div>
            <label className="text-sm font-medium text-ink">Completion status</label>
            <select value={newEdu.completion_status} onChange={(e) => setNewEdu({ ...newEdu, completion_status: e.target.value })} data-testid="edu-completion-status"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt">
              <option value="completed">Completed</option><option value="in_progress">Currently studying</option><option value="partial">Partially completed</option><option value="left_before_completion">Left before completion</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-ink">Qualification status</label>
            <select value={newEdu.award_status} onChange={(e) => setNewEdu({ ...newEdu, award_status: e.target.value })} data-testid="edu-award-status"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt">
              <option value="awarded">Qualification awarded</option><option value="not_awarded">Study completed, not awarded</option><option value="no_formal_diploma">No formal diploma</option><option value="documents_unavailable">Documents unavailable</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-ink">Evidence</label>
            <select value={newEdu.evidence_status} onChange={(e) => setNewEdu({ ...newEdu, evidence_status: e.target.value })} data-testid="edu-evidence-status"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt">
              <option value="official_documents">Diploma and transcript available</option><option value="transcript_only">Transcript available</option><option value="other_evidence">Other official evidence</option><option value="documents_unavailable">Documents unavailable</option><option value="unknown">Not sure</option>
            </select>
          </div>
        </div>
        <button onClick={addEdu} data-testid="edu-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-surface">
          <Plus className="h-4 w-4" /> Add education record
        </button>
      </Card>
    </div>
  );
}
